import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useDeviceLinksSuspense } from 'src/Libs/installations/hooks'
import { syncGridAndSystemProtectionSettings } from '../api/sync-grid-and-system-protection-settings.api'
import { getGridAndSystemProtectionQueryKey } from './use-grid-and-system-protection-query'
import { usePortalMutation } from 'src/Libs/api'

type UseGridAndSystemProtectionSyncMutation = {
  syncSettings: () => void
  isLoading?: boolean
  isError?: boolean
  isSuccess: boolean
}

const useGridAndSystemProtectionSyncMutation = (): UseGridAndSystemProtectionSyncMutation => {
  const { installationId } = useParams<{ installationId: string }>()
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  const { deviceLinks } = useDeviceLinksSuspense(installationId)
  const emcStorageId = deviceLinks?.find(link => link.deviceType === 'emc')?.deviceId || null
  const storageIds = deviceLinks?.filter(link => link.deviceType === 'storage').map(link => link.deviceId) ?? []

  const syncedInstallationId = useRef<string | null>(null)

  const {
    mutate,
    isPending,
    isError,
    isSuccess: mutationSuccess,
  } = usePortalMutation({
    mutationFn: () => syncGridAndSystemProtectionSettings(emcStorageId, authFetch),
    onSuccess: () => {
      syncedInstallationId.current = installationId ?? null
      enqueueSuccessStack(t('system-settings.grid-and-system-protection-configuration.api.sync-success-message'))

      for (const storageId of storageIds) {
        queryClient.invalidateQueries({ queryKey: getGridAndSystemProtectionQueryKey(storageId) })
      }
    },
    onError: () => {
      console.error('Error syncing grid and system protection settings')
      enqueueErrorStack(t('system-settings.grid-and-system-protection-configuration.api.sync-error-message'))
    },
  })

  const isSuccess = mutationSuccess && syncedInstallationId.current === installationId

  return { syncSettings: mutate, isLoading: isPending, isError, isSuccess }
}

export default useGridAndSystemProtectionSyncMutation
